import heic2any from "heic2any";

// Some browsers/OSes report an empty or generic file.type for HEIC/HEIF
// photos, so fall back to checking the filename extension too.
export function isHeicFile(file) {
  return file.type === 'image/heic' || file.type === 'image/heif' || /\.hei[cf]$/i.test(file.name);
}

// Function to format the current date to "YYYYMMDD,HH:MM:SS+0000"
export function formatCurrentTime() {
  const pad = (num, size) => {
    let s = num.toString();
    while (s.length < size) s = "0" + s;
    return s;
  };

  const date = new Date();
  const year = date.getUTCFullYear();
  const month = pad(date.getUTCMonth() + 1, 2);
  const day = pad(date.getUTCDate(), 2);
  const hours = pad(date.getUTCHours(), 2);
  const minutes = pad(date.getUTCMinutes(), 2);
  const seconds = pad(date.getUTCSeconds(), 2);

  return `${year}${month}${day},${hours}:${minutes}:${seconds}+0000`;
}
// Function to parse a formatted date string and convert it to local time
export function formattedDate(dateString) {
  if (typeof dateString !== 'string') {
    throw new Error("Invalid input, expected a string in the format 'YYYYMMDD,HH:MM:SS+0000'");
  }

  const parseDate = (dateStr) => {
    const year = dateStr.slice(0, 4);
    const month = dateStr.slice(4, 6);
    const day = dateStr.slice(6, 8);
    const time = dateStr.slice(9, 17).split(':');

    // Ensure the date is valid
    if (!year || !month || !day || time.length !== 3) {
      throw new Error("Invalid date string format. Expected 'YYYYMMDD,HH:MM:SS+0000'");
    }

    return new Date(Date.UTC(year, month - 1, day, time[0], time[1], time[2]));
  };

  try {
    const date = parseDate(dateString);

    // Check if the date is valid
    if (isNaN(date.getTime())) {
      throw new Error("Invalid date constructed from the string.");
    }

    // Format the date to user's local timezone
    const options = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true
    };

    return date.toLocaleString(undefined, options);
  } catch (error) {
    console.error("Error formatting date:", error.message);
    return "Invalid date";
  }
}
// Function to encode a file as a base64 string
export const encodeFileAsBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve(reader.result.split(',')[1]);
    };
    reader.onerror = (error) => {
      reject(error);
    };
    reader.readAsDataURL(file);
  });
};

// Function to convert HEIC to JPEG
export const convertHEICToJPEG = (blob) => {
  return heic2any({
    blob,
    toType: "image/jpeg",
  }).then((conversionResult) => {
    return conversionResult;
  }).catch((error) => {
    console.error(error);
    return null; // Handle errors as needed
  });
};

// Resize + re-encode an image so every upload is a predictably lightweight
// JPEG, regardless of what the phone/camera originally produced. 1600px on
// the long edge is still plenty of resolution for OCR/label text and model
// inference, while keeping files small enough that the pipeline stays fast.
// We always re-encode (not just when oversized) because a "small" image can
// still be a multi-MB PNG/HEIC-derived file with no useful extra detail.
export const resizeImage = (file, maxDimension = 1600, targetMaxBytes = 1.2 * 1024 * 1024) => {
  return new Promise((resolve) => {
    if (!file.type || !file.type.startsWith('image/')) {
      resolve(file);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        let newWidth = img.width;
        let newHeight = img.height;

        if (newWidth > maxDimension || newHeight > maxDimension) {
          if (newWidth >= newHeight) {
            newHeight = Math.round((newHeight * maxDimension) / newWidth);
            newWidth = maxDimension;
          } else {
            newWidth = Math.round((newWidth * maxDimension) / newHeight);
            newHeight = maxDimension;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = newWidth;
        canvas.height = newHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, newWidth, newHeight);

        const encodeAt = (quality) => new Promise((res) => canvas.toBlob(res, 'image/jpeg', quality));

        (async () => {
          let quality = 0.85;
          let blob = await encodeAt(quality);
          // Step quality down instead of just capping dimensions, so a busy/
          // detailed photo that compresses poorly still lands under the target.
          while (blob && blob.size > targetMaxBytes && quality > 0.5) {
            quality -= 0.1;
            blob = await encodeAt(quality);
          }
          if (!blob) {
            resolve(file);
            return;
          }
          const newName = file.name.replace(/\.[^/.]+$/, '') + '.jpg';
          resolve(new File([blob], newName, { type: 'image/jpeg', lastModified: file.lastModified }));
        })();
      };
      // If the browser can't decode this image (e.g. HEIC without a JPEG
      // fallback), fall back to the original file instead of rejecting and
      // taking down the whole upload batch.
      img.onerror = () => resolve(file);
      img.src = reader.result;
    };
    reader.onerror = () => resolve(file);
    reader.readAsDataURL(file);
  });
};

// Function to generate a UUID
export function extractErrorMessage(err, fallback) {
  const data = err?.response?.data;
  const serverMessage = typeof data === 'string' ? data : data?.error;
  return serverMessage || err?.message || fallback;
}

export function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export const customerHasShopify = (customer) => {
  if (!customer.destinations) {
    return false
  }
  return customer.destinations.filter(destination => destination.type === 1).length > 0;
}

export const customerHasSmartsheet = (customer) => {
  if (!customer.smartsheetSheetId) {
    return false
  }
  return true;
}

export const customerHasEbay = (customer) => {
  if (!customer.destinations) {
    return false
  }
  return customer.destinations.filter(destination => destination.type === 2).length > 0;
}

export const customerHasGoogleMerchant = (customer) => {
  if (!customer.destinations) return false;
  return customer.destinations.some(d => d.type === 3 && d.storeId && d.storeKey);
}

export const customerHasEtsy = (customer) => {
  if (!customer.destinations) {
    return false
  }
  return customer.destinations.filter(destination => destination.type === 5).length > 0;
}
// Get the file extension for a given image type
function getImageExtension(imageType) {
  return 'jpg';
}

// Build the image path
function buildImagePath(customerId, imageType, docId, variant = 'original', imageNumber, ext = 'jpg') {
  const base = `customers/${customerId}/${imageType}/${docId}/${variant}/`;
  const name = imageNumber !== undefined ? `image${imageNumber}.${ext}` : `image.${ext}`;
  return base + name;
}

// Get the full image URL
function getFullImageUrl(customerId, productDoc, imageIndex, imageType) {
  const imageBucketBaseUrl = 'https://storage.googleapis.com/';
  const bucketName = process.env.REACT_APP_STORAGE_BUCKET;
  const path = buildImagePath(customerId, "images", productDoc, imageType, imageIndex, getImageExtension(imageType));
  return `${imageBucketBaseUrl}${bucketName}/${path}`;
}

export function originalImageUrlString(customerId, productId, index) {
  return getFullImageUrl(customerId, productId, index, "original");
}

export function noBackgroundUrlString(customerId, productId, index) {
  return getFullImageUrl(customerId, productId, index, "noBackground");
}

export function thumbnailImageUrlString(customerId, productId, index) {
  return getFullImageUrl(customerId, productId, index, "thumbnail");
}

export function generateImageUrls(customerId, productId, expectedImageCount) {
  // Return empty arrays if inputs are invalid
  if (!customerId || !productId || expectedImageCount === undefined) {
      return {
          originalImageUrls: [],
          noBgImageUrls: [],
          thumbnailUrls: [],
      };
  }

  const originalImageUrls = [];
  const noBgImageUrls = [];
  const thumbnailUrls = [];

  for (let i = 0; i < expectedImageCount; i++) {
      originalImageUrls.push(getFullImageUrl(customerId, productId, i, "original"));
      noBgImageUrls.push(getFullImageUrl(customerId, productId, i, "noBackground"));
      thumbnailUrls.push(getFullImageUrl(customerId, productId, i, "thumbnail"));
  }

  return {
      originalImageUrls,
      noBgImageUrls,
      thumbnailUrls,
  };
}